import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiFilter } from 'react-icons/fi';
import { useVaccinationReminders } from '../../hooks/useReports';
import {
    EmptyState,
    SkeletonTableRow,
    SkeletonMetricCard,
    useToast,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import { formatDateDisplay } from '../../utils/dates';
import { telLink } from '../../utils/format';
import { APP_NAME } from '../../config';
import { buildPdfReport } from '../../utils/pdfReport';
import './UpcomingVaccines.css';

// Contact-outcome options for the per-row Action dropdown. Order
// matters — it drives the rendered <option> order — and the keys
// double as CSS modifier suffixes (.uv-action--<key>).
const ACTION_OPTIONS = [
    { key: 'pending', label: 'Pending' },
    { key: 'called-confirmed', label: 'Called - Confirmed' },
    { key: 'called-no-answer', label: 'Called - No Answer' },
    { key: 'unreachable', label: 'Unreachable' },
];

// Persist contact-outcome choices per (child, dose) across navigations
// and reloads. localStorage is sufficient until the backend exposes a
// real contact-tracking endpoint; the key is versioned so a future
// schema change can ignore stale rows. ponytail: per-browser, no
// multi-device sync — upgrade when backend support lands.
const ACTION_STORAGE_KEY = 'phm:uv-actions:v1';

const loadStoredActions = () => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.localStorage.getItem(ACTION_STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

/**
 * UpcomingVaccines tab — Reminders → Vaccinations.
 *
 * Per spec: shows only Due + Upcoming rows. Overdue is intentionally
 * excluded from both the BE payload (scope=reminders) and any client
 * filtering below — no row or count for it ever shows on this page.
 *
 * Two-zone layout:
 *   1. Logistical forecasting — two tiles (Due + Upcoming) with the
 *      total dose count and per-vaccine chips.
 *   2. Patient tracing table — every child with a qualifying dose,
 *      with a dynamic DUE urgency pill and a per-row action dropdown
 *      for contact outcome tracking.
 */

// Default-after threshold in days — must match the backend's Due window
// (vaccine_service.calculate_dynamic_status: -30 <= diff_days < 0 → "Due").
const DEFAULT_GRACE_DAYS = 30;

// Compute whole-day delta between two date inputs, ignoring time-of-day.
// ponytail: local-tz math, fine for a clinic-day display.
const dayDelta = (scheduled) => {
    if (!scheduled) return null;
    const a = new Date(scheduled);
    const b = new Date();
    a.setHours(0, 0, 0, 0);
    b.setHours(0, 0, 0, 0);
    return Math.floor((a - b) / 86400000);
};

// Render the dynamic status pill. "Due" rows show days left in the
// grace window; everything else falls back to the plain label.
const renderStatus = (r) => {
    const label = r.status || '-';
    const baseClass = `uv-status uv-status--${label.toLowerCase()}`;
    if (label !== 'Due') {
        return <span className={baseClass}>{label}</span>;
    }
    const diff = dayDelta(r.scheduledDate);
    // diff in [-30, 0]; daysUntilDefault in [0, 30]. null = no date.
    const daysUntilDefault = diff === null ? null : DEFAULT_GRACE_DAYS + diff;
    // All Due-status pills share one colour regardless of urgency tier —
    // only the text differentiates (DEFAULTED / N Days Until Default).
    const urgency = 'due';
    let text;
    if (daysUntilDefault === null) {
        text = 'DUE';
    } else if (daysUntilDefault <= 0) {
        text = 'DUE — DEFAULTED';
    } else if (daysUntilDefault <= 3) {
        text = `DUE — ${daysUntilDefault} Day${daysUntilDefault === 1 ? '' : 's'} Until Default`;
    } else if (daysUntilDefault <= 7) {
        text = `DUE — ${daysUntilDefault} Days Until Default`;
    } else {
        text = `DUE — ${daysUntilDefault} Days Until Default`;
    }
    return (
        <span className={`${baseClass} uv-status--${urgency}`} title={`Default after ${DEFAULT_GRACE_DAYS}-day grace window`}>
            {text}
        </span>
    );
};

// Two-tile summary: big count + per-vaccine chips for a status bucket.
// Hoisted outside the component so useMemo can depend on it without a
// stale-closure warning. Pure function — safe to reference from memos.
const bucketBy = (rows, predicate) => {
    const counts = new Map();
    for (const r of rows) {
        if (!predicate(r) || !r.vaccineName) continue;
        counts.set(r.vaccineName, (counts.get(r.vaccineName) || 0) + 1);
    }
    const byVaccine = Array.from(counts.entries())
        .map(([vaccine, doses]) => ({ vaccine, doses }))
        .sort((a, b) => b.doses - a.doses || a.vaccine.localeCompare(b.vaccine));
    return {
        count: byVaccine.reduce((s, x) => s + x.doses, 0),
        byVaccine,
    };
};

// Two-tile summary: big count + per-vaccine chips for a status bucket.
// Clicking a tile sets the queue filter to that bucket; clicking the
// already-active tile clears the filter back to "all".
const SummaryTile = ({ kind, count, byVaccine, active, onToggle }) => (
    <button
        type="button"
        className={`uv-tile uv-tile--${kind}${active ? ' uv-tile--active' : ''}`}
        onClick={onToggle}
        aria-pressed={active}
        title={active ? 'Click to clear filter' : `Filter table to ${kind === 'due' ? 'due' : 'upcoming'}`}
    >
        <header className="uv-tile__header">
            <span className="uv-tile__count">{count}</span>
            <span className="uv-tile__label">
                {kind === 'due' ? 'Due' : 'Upcoming'}
            </span>
            {active && (
                <span className="uv-tile__filter-tag" aria-hidden="true">
                    <FiFilter size={11} /> filtered
                </span>
            )}
        </header>
        <div
            className="uv-tile__chips"
            onClick={(e) => e.stopPropagation()}
        >
            {byVaccine.length === 0 ? (
                <span className="uv-tile__chips-empty">None</span>
            ) : (
                byVaccine.map(({ vaccine, doses }) => (
                    <span key={vaccine} className="uv-tile__chip">
                        <span className="uv-tile__chip-name">{vaccine}</span>
                        <span className="uv-tile__chip-count">×{doses}</span>
                    </span>
                ))
            )}
        </div>
    </button>
);

const UpcomingVaccines = ({ onExportChange, initialFilter = 'all' }) => {
    const { data, loading, error } = useVaccinationReminders();
    const toast = useToast();

    const rows = useMemo(() => data?.rows || [], [data]);

    // Per-row contact-outcome tracking. Persisted to localStorage so
    // the choice survives route changes and reloads; the store key is
    // the same composite used for React keys so it stays stable across
    // re-fetches. Syncs on every change — quota errors are swallowed.
    const [actions, setActions] = useState(loadStoredActions);
    const setAction = (key, value) =>
        setActions((prev) => {
            const next = { ...prev, [key]: value };
            try {
                window.localStorage.setItem(ACTION_STORAGE_KEY, JSON.stringify(next));
            } catch {
                /* private mode / quota — fall back to in-memory only */
            }
            return next;
        });

    // Top zone — two summary tiles (Due + Upcoming). Each bucket is
    // rows with that status, aggregated by vaccineName into chips.
    // Strict status match — Overdue rows are filtered out at the BE
    // scope level (scope=reminders) and never appear here.
    const dueSummary = useMemo(
        () => bucketBy(rows, (r) => r.status === 'Due'),
        [rows],
    );
    const upcomingSummary = useMemo(
        () => bucketBy(rows, (r) => r.status === 'Upcoming'),
        [rows],
    );

    // Bottom zone — flat child queue, sorted by scheduled date ascending.
    // Default shows both Due + Upcoming in one table; the tiles above
    // act as a toggle filter — click Due/Upcoming to scope the table,
    // click the active tile again to clear. Overdue rows are absent
    // here (scope=reminders narrows the source payload to Due + Upcoming).
    const [filter, setFilter] = useState(
        initialFilter === 'due' || initialFilter === 'upcoming' ? initialFilter : 'all',
    ); // 'all' | 'due' | 'upcoming'
    const toggleFilter = (kind) =>
        setFilter((cur) => (cur === kind ? 'all' : kind));

    const queue = useMemo(
        () => {
            const filtered = filter === 'due'
                ? rows.filter((r) => r.status === 'Due')
                : filter === 'upcoming'
                    ? rows.filter((r) => r.status === 'Upcoming')
                    : rows;
            return [...filtered].sort((a, b) =>
                (a.scheduledDate || '').localeCompare(b.scheduledDate || ''),
            );
        },
        [rows, filter],
    );

    // Group the queue by child so the patient-tracing table can use
    // rowSpan on the child-info columns (reg, name, phone) and only
    // repeat the dose columns (vaccine, date, status, action). When a
    // child has many due doses, the reg/name/phone appear once and the
    // dose list fans out below — same pattern as Reminders → Milestones.
    // The global sort is by scheduledDate asc, so within a group the
    // rows already read earliest-dose-first.
    const groupedQueue = useMemo(() => {
        const map = new Map();
        for (const r of queue) {
            if (!map.has(r.childId)) map.set(r.childId, []);
            map.get(r.childId).push(r);
        }
        return Array.from(map.values()).map((rows) => ({
            childId: rows[0].childId,
            rows,
        }));
    }, [queue]);

    // PDF export — mirrors whatever the user is currently looking at
    // (queue honours the active Due/Upcoming filter), so exporting from
    // a filtered view yields a filtered PDF. Two sections: (1) summary
    // table — status × vaccine × dose count, honouring the filter; then
    // (2) the patient tracing queue with the contact-outcome Action
    // column dropped in from local state. ponytail: full set vs view is
    // a UX call; view-matches-what-you-see is the principle of least
    // surprise and is consistent with how VaccineSummaries exports its
    // current action queue.
    const exportPDF = useCallback(() => {
        const stamp = new Date().toISOString().split('T')[0];
        const visibleRows = filter === 'due'
            ? rows.filter((r) => r.status === 'Due')
            : filter === 'upcoming'
                ? rows.filter((r) => r.status === 'Upcoming')
                : rows;
        const distinctVaccines = new Set(
            visibleRows.map((r) => r.vaccineName).filter(Boolean),
        ).size;
        // Summary table shape depends on the active filter:
        //   filter=all     → one row per vaccine with its TOTAL dose
        //                    count across Due + Upcoming combined. No
        //                    Status column — the bucket has been
        //                    collapsed into the total.
        //   filter=due     → one row per vaccine that has ≥1 Due dose.
        //   filter=upcoming→ one row per vaccine that has ≥1 Upcoming.
        // No Status column in any case.
        let summaryBody;
        if (filter === 'all') {
            summaryBody = Object.entries(
                visibleRows.reduce((acc, r) => {
                    const name = r.vaccineName || 'Unknown';
                    acc[name] = (acc[name] || 0) + 1;
                    return acc;
                }, {}),
            )
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([vaccine, doses]) => [vaccine, doses]);
        } else {
            const status = filter === 'due' ? 'Due' : 'Upcoming';
            summaryBody = Object.entries(
                visibleRows
                    .filter((r) => r.status === status)
                    .reduce((acc, r) => {
                        const name = r.vaccineName || 'Unknown';
                        acc[name] = (acc[name] || 0) + 1;
                        return acc;
                    }, {}),
            )
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([vaccine, doses]) => [vaccine, doses]);
        }

        const titleByFilter =
            filter === 'due'
                ? 'Vaccination Reminders Report - For Due vaccines Only'
                : filter === 'upcoming'
                    ? 'Vaccination Reminders Report - For Upcoming vaccines Only'
                    : 'Vaccination Reminders Report';
        const actionLabel = (key) =>
            (ACTION_OPTIONS.find((o) => o.key === key) || ACTION_OPTIONS[0]).label;
        const actionFor = (r) => {
            const rowKey = `${r.childId}-${r.frontendId || `${r.vaccineName}-${r.scheduledDate}`}`;
            return actionLabel(actions[rowKey] || 'pending');
        };

        buildPdfReport({
            title: titleByFilter,
            subtitle: `${visibleRows.length} doses needed across ${distinctVaccines} vaccine${distinctVaccines === 1 ? '' : 's'}`,
            filters: [],
            sections: [
                {
                    head: ['Vaccine Type', 'Doses Needed'],
                    body: summaryBody,
                    accent: [14, 165, 233], // sky-500
                },
                {
                    heading: 'Patient Tracing Queue',
                    head: ['Reg No', 'Child Name', 'Vaccine', 'Scheduled Date', 'Status', 'Action', "Mother's Phone"],
                    body: queue.map((r) => [
                        r.childNo || '-',
                        r.childName || '-',
                        r.vaccineName || '-',
                        r.scheduledDate ? formatDateDisplay(r.scheduledDate) : '-',
                        r.status || '-',
                        actionFor(r),
                        r.contactNumber || '-',
                    ]),
                    accent: [59, 130, 246], // blue-500
                },
            ],
            fileName: `Vaccination_Reminders_${stamp}.pdf`,
            toast,
        });
    }, [rows, queue, filter, toast, actions]);

    // Publish the export trigger + canExport flag up to the parent
    // (Reminders) so it can render the Export PDF button in the tabs
    // row instead of inside this tab. Callback-on-change (not a ref)
    // so the parent re-renders when canExport flips. ponytail: a
    // callback that fires on every relevant change is a few lines
    // lighter than a ref + state bridge and avoids the "ref updated
    // but no re-render" trap.
    useEffect(() => {
        onExportChange?.({ exportPDF, canExport: queue.length > 0 });
    }, [onExportChange, exportPDF, queue.length]);

    if (loading) {
        return (
            <div className="uv-body">
                <div className="uv-summary">
                    <SkeletonMetricCard />
                    <SkeletonMetricCard />
                </div>
                <div className="uv-card">
                    <table className="uv-table">
                        <thead>
                            <tr>
                                <th>Reg No</th>
                                <th>Child</th>
                                <th>Type of vaccine</th>
                                <th>Scheduled Date</th>
                                <th>Status</th>
                                <th>Action</th>
                                <th>Mother's Phone</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <SkeletonTableRow key={i} cols={7} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="uv-body">
                <EmptyState
                    illustration="error"
                    title="Couldn't load reminders"
                    description={error}
                    action={
                        <PageButton
                            type="button"
                            variant="primary"
                            onClick={() => window.location.reload()}
                        >
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    return (
        <div className="uv-body">
            {/* Zone 1 — Two summary tiles: Due + Upcoming, with per-vaccine chips.
               Clicking a tile filters the table below; click again to clear. */}
            <section className="uv-summary" aria-label="Vaccination reminders summary">
                <SummaryTile
                    kind="due"
                    count={dueSummary.count}
                    byVaccine={dueSummary.byVaccine}
                    active={filter === 'due'}
                    onToggle={() => toggleFilter('due')}
                />
                <SummaryTile
                    kind="upcoming"
                    count={upcomingSummary.count}
                    byVaccine={upcomingSummary.byVaccine}
                    active={filter === 'upcoming'}
                    onToggle={() => toggleFilter('upcoming')}
                />
            </section>

            {/* Zone 2 — Patient Tracing Table */}
            <section className="uv-card" aria-label="Upcoming vaccinations list">
                <div className="uv-table-wrap">
                    <table className="uv-table">
                        <thead>
                            <tr>
                                <th>Reg No</th>
                                <th>Child Name</th>
                                <th>Type of vaccine</th>
                                <th>Scheduled Date</th>
                                <th>Status</th>
                                <th>Action</th>
                                <th>Mother's Phone</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedQueue.map((group) =>
                                group.rows.map((r, idx) => {
                                    const isFirst = idx === 0;
                                    const rowKey = `${r.childId}-${r.frontendId || `${r.vaccineName}-${r.scheduledDate}`}`;
                                    const actionKey = actions[rowKey] || 'pending';
                                    return (
                                        <tr key={rowKey}>
                                            {isFirst && (
                                                <>
                                                    <td className="uv-childno" rowSpan={group.rows.length}>
                                                        {r.childNo || '-'}
                                                    </td>
                                                    <td className="fw-bold" rowSpan={group.rows.length}>
                                                        {r.childId ? (
                                                            <Link
                                                                to={`/vaccination/${r.childId}`}
                                                                className="uv-link"
                                                                title="Open vaccination list"
                                                            >
                                                                {r.childName || '-'}
                                                            </Link>
                                                        ) : (
                                                            r.childName || '-'
                                                        )}
                                                    </td>
                                                </>
                                            )}
                                            <td>{r.vaccineName || '-'}</td>
                                            <td>{formatDateDisplay(r.scheduledDate) || '-'}</td>
                                            <td>{renderStatus(r)}</td>
                                            <td>
                                                <select
                                                    className={`uv-action uv-action--${actionKey}`}
                                                    value={actionKey}
                                                    onChange={(e) =>
                                                        setAction(rowKey, e.target.value)
                                                    }
                                                    aria-label="Contact outcome"
                                                >
                                                    {ACTION_OPTIONS.map((opt) => (
                                                        <option key={opt.key} value={opt.key}>
                                                            {opt.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </td>
                                            {isFirst && (
                                                <td rowSpan={group.rows.length}>
                                                    {r.contactNumber ? (
                                                        <a
                                                            href={telLink(r.contactNumber)}
                                                            className="uv-call"
                                                            title="Call caregiver"
                                                        >
                                                            <FiPhone size={13} />
                                                            {r.contactNumber}
                                                        </a>
                                                    ) : (
                                                        <span className="uv-call uv-call--muted">—</span>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                }),
                            )}
                            {groupedQueue.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="uv-empty-cell">
                                        <EmptyState
                                            illustration="filter"
                                            compact
                                            title={
                                                filter === 'all'
                                                    ? 'No upcoming vaccinations'
                                                    : `No ${filter} vaccinations`
                                            }
                                            description={
                                                filter === 'all'
                                                    ? 'No children are due in the upcoming window.'
                                                    : 'Click the active tile above to clear the filter.'
                                            }
                                        />
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default UpcomingVaccines;
